import React from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../hooks/usePageTitle';
import '../styles/pages/PrivacyPolicyPage.css';

const PrivacyPolicyPage = () => {
  usePageTitle('Privacy Policy');
  return (
    <div className="policy-page-container">
      <div className="return-link-container return-link-top">
        <Link to="/" className="return-link">← Return to The Icing Calculator</Link>
      </div>
      <h1>Privacy Policy for The Icing Calculator</h1>
      <p>Last updated: August 19, 2026</p>

      <section>
        <h2>Introduction</h2>
        <p>
          Welcome to The Icing Calculator. We are committed to protecting your privacy.
          This Privacy Policy explains how we collect, use, disclose, and safeguard your
          information when you visit our website at{' '}
          <a href="https://theicingcalculator.com">theicingcalculator.com</a> and use our services.
          Please read this policy carefully. If you disagree with its terms, please discontinue use of the site.
        </p>
      </section>

      <section>
        <h2>Information We Collect</h2>

        <h3>Basic Calculator</h3>
        <p>
          The Basic Calculator does not require an account to use. We do not collect your icing
          calculations, cookie dimensions, or any inputs you enter. Those are never sent to our
          servers. However, your input values (reference cookie and the cookie sizes you enter) are
          saved locally on your device using browser cookies and localStorage so that the calculator
          remembers your last entries between visits. This data never leaves your device.
        </p>
        <p>
          When you click the <strong>Copy Results</strong> button in the Basic Calculator,
          we record a usage event. This event log includes:
        </p>
        <ul>
          <li>The type of action taken (e.g., "copy results")</li>
          <li>The page URL at the time of the action</li>
          <li>A timestamp</li>
          <li>Your user ID and email address, if you're signed in</li>
        </ul>

        <h3>Account Registration</h3>
        <p>
          An account is not required to use The Icing Calculator's core features. Both the Basic
          and Pro Calculators are free to use without signing in. An account is only needed to save
          Icing Plans, export to PDF or Excel, or use certain AI-powered features. Creating an
          account is free. You can sign in with Google or with an email and password.
          When you create an account, we collect and store:
        </p>
        <ul>
          <li>Your email address</li>
          <li>Your name (if provided by Google during sign-in)</li>
          <li>Account creation date and last sign-in date</li>
          <li>Your subscription status (free trial, active, expired, etc.)</li>
        </ul>

        <h3>Pro Calculator: Saved Plans</h3>
        <p>
          If you choose to save an Icing Plan in the Pro Calculator, we store the following
          on our secure servers so you can access it across devices:
        </p>
        <ul>
          <li>Plan name</li>
          <li>Cookie names, dimensions, and counts</li>
          <li>Color names and percentages</li>
          <li>Order quantities</li>
          <li>Reference cookie data</li>
          <li>Date created and last modified</li>
        </ul>
        <p>
          Plans are associated with your account and are only accessible by you.
          Unsaved calculations are never transmitted to our servers.
        </p>

        <h3>Pro Calculator: Usage Events</h3>
        <p>
          When you use certain features of the Pro Calculator, we record a usage event to help
          us understand how the tool is being used and improve the service. The following actions
          are logged:
        </p>
        <ul>
          <li><strong>Copy Results</strong>: when you copy your icing totals to the clipboard</li>
          <li><strong>Export to Excel</strong>: when you download your results as a spreadsheet</li>
          <li><strong>Export to PDF</strong>: when you download your results as a PDF</li>
        </ul>
        <p>Each usage event record includes:</p>
        <ul>
          <li>Your user ID and email address</li>
          <li>The type of action taken</li>
          <li>The name and ID of the currently loaded plan, if any</li>
          <li>The calculator and page URL at the time of the action</li>
          <li>A timestamp</li>
        </ul>

        <h3>Subscription and Payment Information</h3>
        <p>
          Pro subscriptions are processed through <strong>Stripe</strong>. We do not collect or
          store your credit card number or full payment details. Stripe provides us with
          a subscription status, subscription ID, and billing period dates, which we store
          to manage your access to Pro features.
        </p>

        <h3>Affiliate Links</h3>
        <p>
          Our site may include affiliate links (e.g., to Amazon). Clicking these links
          takes you to a third-party website. We do not track which affiliate links you
          click or what you purchase. Those third parties have their own privacy policies.
        </p>
      </section>

      <section>
        <h2>How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Create and manage your account</li>
          <li>Store and retrieve your saved Icing Plans across devices</li>
          <li>Manage your Pro subscription and access permissions</li>
          <li>Process payments and handle billing inquiries (via Stripe)</li>
          <li>Analyze how features are used so we can improve the service</li>
          <li>Respond to support requests</li>
          <li>Send transactional emails related to your account (e.g., billing notifications)</li>
        </ul>
        <p>
          We do not sell your personal information. We do not use your data for advertising
          or share it with marketing third parties.
        </p>
      </section>

      <section>
        <h2>Disclosure of Your Information</h2>
        <p>Your information may be shared only in the following circumstances:</p>
        <ul>
          <li>
            <strong>Service Providers:</strong> We use Supabase for database hosting and
            authentication, and Stripe for payment processing. Both are contractually
            obligated to protect your data and may only use it to provide services on our behalf.
          </li>
          <li>
            <strong>Legal Requirements:</strong> We may disclose your information if required
            to do so by law or in response to valid legal process (such as a subpoena or court order).
          </li>
          <li>
            <strong>Protection of Rights:</strong> We may disclose information where we believe
            it is necessary to investigate, prevent, or take action regarding illegal activities,
            suspected fraud, or situations involving potential threats to safety.
          </li>
        </ul>
      </section>

      <section>
        <h2>Data Retention and Deletion</h2>
        <p>
          We retain your account information and saved plans for as long as your account is active.
          Usage event logs are retained for internal analytics purposes.
        </p>
        <p>
          You can delete your account and all associated data at any time from your{' '}
          <Link to="/account">Account page</Link>. Deleting your account permanently removes:
        </p>
        <ul>
          <li>Your account profile (email address, name, sign-in history)</li>
          <li>All saved Icing Plans</li>
          <li>Your subscription record and billing history stored on our servers</li>
          <li>All usage event logs associated with your account</li>
        </ul>
        <p>
          Note: Payment history managed by Stripe is subject to Stripe's own data retention policies
          and may not be immediately removed from their systems.
        </p>
      </section>

      <section>
        <h2>Security of Your Information</h2>
        <p>
          We use industry-standard security practices, including encrypted data transmission (HTTPS)
          and secure database hosting through Supabase. While we take reasonable precautions,
          no method of electronic storage or transmission is 100% secure, and we cannot
          guarantee absolute security.
        </p>
      </section>

      <section>
        <h2>Policy for Children</h2>
        <p>
          Our service is not directed to children under the age of 13. We do not knowingly
          collect personal information from children under 13. If you become aware that a child
          has provided us with personal data, please contact us and we will take steps to delete it.
        </p>
      </section>

      <section>
        <h2>Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of significant
          changes by updating the "Last updated" date at the top of this page. Your continued
          use of the service after any changes constitutes your acceptance of the new policy.
        </p>
      </section>

      <section>
        <h2>Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy or how your data is handled,
          please contact us at:
          <br />
          Email: <a href="mailto:support@theicingcalculator.com">support@theicingcalculator.com</a>
        </p>
      </section>
      <div className="return-link-container return-link-bottom">
        <Link to="/" className="return-link">← Return to The Icing Calculator</Link>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
